import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Shield, Users, CreditCard, Share2, Settings,
  Loader2, Search, RotateCcw, KeyRound, XCircle,
  LogOut, ChevronDown, ChevronUp, Eye, EyeOff,
  TrendingUp, Crown, Star, Clock, CheckCircle2,
  Trash2, ShieldOff, ShieldCheck, UserPlus, Tag, Mail, Award, Gift,
} from 'lucide-react';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem('kylo_admin_token');
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

/* ───── Stat Card ───── */
const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card className="bg-[#12121a] border-[#1e1e2e] p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
      </div>
      <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

/* ───── Overview Tab ───── */
const OverviewTab = ({ stats, loading }) => {
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <SEO title="Admin Dashboard" description="Poettik admin panel — restricted access." noindex />
      <StatCard label="Total Users" value={stats.total_users} icon={Users} color="bg-blue-500/15 text-blue-400" />
      <StatCard label="Active Subscriptions" value={stats.active_subscriptions} icon={CreditCard} color="bg-emerald-500/15 text-emerald-400" />
      <StatCard label="Trial Users" value={stats.trial_users} icon={Clock} color="bg-amber-500/15 text-amber-400" />
      <StatCard label="Basic Users" value={stats.basic_users} icon={Star} color="bg-cyan-500/15 text-cyan-400" />
      <StatCard label="Pro Users" value={stats.pro_users} icon={Crown} color="bg-purple-500/15 text-purple-400" />
      <StatCard label="Affiliates" value={stats.total_affiliates} icon={Share2} color="bg-pink-500/15 text-pink-400" />
    </div>
  );
};

/* ───── Create User Modal ───── */
const CreateUserModal = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [planType, setPlanType] = useState('basic');
  const [durationDays, setDurationDays] = useState(30);
  const [comp, setComp] = useState(true);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 12; i++) out += chars[Math.floor(Math.random() * chars.length)];
    setPassword(out);
  };

  const reset = () => {
    setName(''); setEmail(''); setPassword('');
    setPlanType('basic'); setDurationDays(30);
    setComp(true); setSendWelcomeEmail(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || password.length < 6) {
      toast.error('Name, email and a 6+ char password are required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/users`,
        {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          password,
          plan_type: planType,
          duration_days: Number(durationDays) || 30,
          comp,
          send_welcome_email: sendWelcomeEmail,
        },
        { headers: authHeaders() }
      );
      toast.success(`Created ${res.data.user.email}${res.data.welcome_email_sent ? ' — welcome email sent' : ''}`);
      reset();
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm overflow-y-auto p-4" data-testid="create-user-modal">
      <div className="w-full max-w-md bg-[#12121a] border border-[#1e1e2e] rounded-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-[#1e1e2e]">
          <div className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">Create user</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="create-user-close">
            <XCircle className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Full name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe"
              className="bg-[#0a0a0f] border-[#1e1e2e] text-white" data-testid="create-user-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com"
              className="bg-[#0a0a0f] border-[#1e1e2e] text-white" data-testid="create-user-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Password</Label>
            <div className="flex gap-2">
              <Input value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 6 characters"
                className="bg-[#0a0a0f] border-[#1e1e2e] text-white font-mono" data-testid="create-user-password" />
              <Button type="button" variant="outline" size="sm" onClick={generatePassword}
                className="border-[#1e1e2e] text-zinc-400 hover:text-white whitespace-nowrap" data-testid="create-user-gen-password">
                Generate
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Plan</Label>
              <select value={planType} onChange={e => setPlanType(e.target.value)}
                className="w-full bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm rounded-md px-3 py-2"
                data-testid="create-user-plan">
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="trial">3-day Trial</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Duration (days)</Label>
              <Input type="number" min="1" value={durationDays}
                onChange={e => setDurationDays(e.target.value)}
                disabled={planType === 'trial'}
                className="bg-[#0a0a0f] border-[#1e1e2e] text-white disabled:opacity-50"
                data-testid="create-user-duration" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={comp} onChange={e => setComp(e.target.checked)}
              className="accent-cyan-500" data-testid="create-user-comp" />
            Comp account (no monthly charge — shown as £0)
          </label>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={sendWelcomeEmail}
              onChange={e => setSendWelcomeEmail(e.target.checked)}
              className="accent-cyan-500" data-testid="create-user-email-toggle" />
            Email the user their login details
          </label>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}
              className="flex-1 border-[#1e1e2e] text-zinc-400 hover:text-white" data-testid="create-user-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} variant="premium" className="flex-1" data-testid="create-user-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UserPlus className="w-4 h-4 mr-1.5" />}
              Create user
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ───── Grant Free Plan inline form ───── */
const GrantPlanForm = ({ user, busy, onConfirm, onCancel }) => {
  const [planType, setPlanType] = useState('pro');
  const [durationDays, setDurationDays] = useState(30);
  const [reason, setReason] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  const presets = [7, 30, 90, 365];

  const submit = (e) => {
    e.preventDefault();
    if (durationDays < 1) {
      toast.error('Duration must be at least 1 day');
      return;
    }
    onConfirm(planType, durationDays, reason.trim(), sendEmail);
  };

  return (
    <form
      onSubmit={submit}
      className="pt-3 border-t border-emerald-500/20 space-y-3 bg-emerald-500/5 -mx-4 px-4 py-4 mt-3"
      data-testid={`grant-plan-form-${user.email}`}
    >
      <p className="text-xs font-semibold text-emerald-300 flex items-center gap-1.5">
        <Gift className="w-3.5 h-3.5" />
        Grant a free plan to {user.email}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Plan</Label>
          <select
            value={planType}
            onChange={(e) => setPlanType(e.target.value)}
            className="w-full bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm rounded-md px-3 py-2"
            data-testid={`grant-plan-type-${user.email}`}
          >
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
          </select>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Duration (days)</Label>
          <Input
            type="number"
            min="1"
            max="3650"
            value={durationDays}
            onChange={(e) => setDurationDays(parseInt(e.target.value || '0', 10))}
            className="bg-[#0a0a0f] border-[#1e1e2e] text-white"
            data-testid={`grant-plan-duration-${user.email}`}
          />
          <div className="flex gap-1.5 mt-1.5">
            {presets.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDurationDays(d)}
                className={`text-[10px] px-2 py-0.5 rounded ${durationDays === d ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#1e1e2e] text-zinc-400 hover:text-white'}`}
              >
                {d === 365 ? '1 yr' : `${d}d`}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-zinc-400 text-xs mb-1 block">Reason (optional, logged)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            placeholder="e.g. Influencer comp, beta tester"
            className="bg-[#0a0a0f] border-[#1e1e2e] text-white"
            data-testid={`grant-plan-reason-${user.email}`}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(e) => setSendEmail(e.target.checked)}
          className="accent-emerald-500"
          data-testid={`grant-plan-email-toggle-${user.email}`}
        />
        Email the user telling them they&apos;ve been upgraded
      </label>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="border-[#1e1e2e] text-zinc-400 hover:text-white"
          size="sm"
          data-testid={`grant-plan-cancel-${user.email}`}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={busy}
          variant="premium"
          size="sm"
          className="bg-emerald-500 hover:bg-emerald-400 text-black"
          data-testid={`grant-plan-confirm-${user.email}`}
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Gift className="w-3.5 h-3.5 mr-1.5" />}
          Grant {planType.toUpperCase()} for {durationDays} days
        </Button>
      </div>
    </form>
  );
};


/* ───── Users Tab ───── */
const UsersTab = ({ onRefresh }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [codes, setCodes] = useState([]);
  const [selectedCode, setSelectedCode] = useState({});
  const [applyingDiscount, setApplyingDiscount] = useState(null);
  const [removingDiscount, setRemovingDiscount] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [togglingAdmin, setTogglingAdmin] = useState(null);
  const [togglingElig, setTogglingElig] = useState(null);
  const [resending, setResending] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [grantOpenFor, setGrantOpenFor] = useState(null);  // user_id currently in grant-plan dialog
  const [granting, setGranting] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/users`, { headers: authHeaders() });
      setUsers(res.data.users);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUsers();
    // Also load active discount codes so we can offer them in the user row
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/discount-codes`, { headers: authHeaders() });
        setCodes((res.data.codes || []).filter(c => c.active));
      } catch { /* non-fatal */ }
    })();
  }, [fetchUsers]);

  const resetPassword = async (userId, email) => {
    setResetting(userId);
    try {
      const res = await axios.post(`${API}/api/admin/users/${userId}/reset-password`, {}, { headers: authHeaders() });
      toast.success(`New password for ${email}: ${res.data.new_password}`, { duration: 15000 });
      if (res.data.email_sent) toast.info('Password also emailed to user');
    } catch { toast.error('Failed to reset password'); }
    finally { setResetting(null); }
  };

  const resendLogin = async (userId, email) => {
    if (!window.confirm(`Send fresh login details to ${email}? This will reset their password and email them the new one.`)) return;
    setResending(userId);
    try {
      const res = await axios.post(`${API}/api/admin/users/${userId}/resend-login`, {}, { headers: authHeaders() });
      toast.success(`New password for ${email}: ${res.data.new_password}`, { duration: 15000 });
      if (res.data.email_sent) {
        toast.info(`Login details emailed to ${email}`);
      } else {
        toast.warning('Email not sent — SMTP may be unconfigured. Share the password manually.', { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend login details');
    } finally { setResending(null); }
  };

  const cancelSub = async (userId, email) => {
    if (!window.confirm(`Cancel the subscription for ${email}? They'll keep access until the end of their current billing period.`)) return;
    setCancelling(userId);
    try {
      await axios.post(`${API}/api/admin/users/${userId}/cancel-subscription`, {}, { headers: authHeaders() });
      toast.success(`Subscription cancelled for ${email}`);
      fetchUsers();
      onRefresh();
    } catch { toast.error('Failed to cancel subscription'); }
    finally { setCancelling(null); }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Permanently DELETE the user ${email}?\n\nThis cancels any active Stripe subscription immediately and removes the user, their saved ideas, daily-usage history and affiliate identity. This cannot be undone.`)) return;
    setDeleting(userId);
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, { headers: authHeaders() });
      toast.success(`User ${email} deleted`);
      fetchUsers();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally { setDeleting(null); }
  };

  const toggleAdmin = async (userId, email, isAdmin) => {
    const verb = isAdmin ? 'remove admin rights from' : 'make an admin of';
    if (!window.confirm(`Are you sure you want to ${verb} ${email}?`)) return;
    setTogglingAdmin(userId);
    try {
      const res = await axios.post(`${API}/api/admin/users/${userId}/toggle-admin`, {}, { headers: authHeaders() });
      toast.success(`${email} is ${res.data.is_admin ? 'now an admin' : 'no longer an admin'}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle admin flag');
    } finally { setTogglingAdmin(null); }
  };

  const grantPlan = async (userId, email, planType, durationDays, reason, sendEmail) => {
    setGranting(userId);
    try {
      const res = await axios.post(
        `${API}/api/admin/users/${userId}/grant-plan`,
        { plan_type: planType, duration_days: durationDays, reason, send_email: sendEmail },
        { headers: authHeaders() }
      );
      const expiry = new Date(res.data.expires_at).toLocaleDateString('en-GB');
      toast.success(`${email} now on ${planType.toUpperCase()} until ${expiry}`);
      setGrantOpenFor(null);
      fetchUsers();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to grant plan');
    } finally {
      setGranting(null);
    }
  };

  const toggleAffiliateEligibility = async (userId, email, current) => {
    const verb = current ? 'remove always-eligible flag from' : 'mark as always eligible';
    if (!window.confirm(`Are you sure you want to ${verb} ${email}?`)) return;
    setTogglingElig(userId);
    try {
      const res = await axios.post(
        `${API}/api/admin/users/${userId}/toggle-affiliate-eligibility`,
        {},
        { headers: authHeaders() }
      );
      const promoted = res.data.promoted_amount > 0
        ? ` £${res.data.promoted_amount.toFixed(2)} released from hold.`
        : '';
      toast.success(
        `${email} ${res.data.affiliate_always_eligible ? 'is now always eligible.' : 'no longer has override.'}${promoted}`
      );
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle eligibility');
    } finally { setTogglingElig(null); }
  };

  const applyDiscount = async (userId, email) => {
    const code = selectedCode[userId];
    if (!code) {
      toast.error('Choose a discount code first');
      return;
    }
    setApplyingDiscount(userId);
    try {
      const res = await axios.post(
        `${API}/api/admin/users/${userId}/apply-discount`,
        { code },
        { headers: authHeaders() }
      );
      toast.success(res.data.message || `Discount applied to ${email}`);
      if (res.data.stripe_error) {
        toast.warning(`Stripe: ${res.data.stripe_error}`, { duration: 8000 });
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setApplyingDiscount(null);
    }
  };

  const removeDiscount = async (userId, email) => {
    setRemovingDiscount(userId);
    try {
      const res = await axios.post(
        `${API}/api/admin/users/${userId}/remove-discount`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Discount removed from ${email}`);
      if (res.data.stripe_error) {
        toast.warning(`Stripe: ${res.data.stripe_error}`, { duration: 8000 });
      }
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove discount');
    } finally {
      setRemovingDiscount(null);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const formatRelative = (iso) => {
    if (!iso) return 'Never';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return 'Never';
    const diffMs = Date.now() - then.getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return then.toLocaleDateString('en-GB');
  };

  const dormantBadge = (lastLoginIso) => {
    if (!lastLoginIso) return null;
    const days = Math.floor((Date.now() - new Date(lastLoginIso).getTime()) / 86_400_000);
    if (days < 14) return null;
    return (
      <span className="ml-2 text-[10px] text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
        DORMANT {days}d
      </span>
    );
  };

  const subLabel = (u) => {
    if (u.subscription_type === 'pro') return <span className="text-purple-400 font-medium">Pro</span>;
    if (u.subscription_type === 'basic') return <span className="text-cyan-400 font-medium">Basic</span>;
    if (u.subscription_type === 'trial') return <span className="text-amber-400 font-medium">Trial</span>;
    return <span className="text-zinc-600">Expired</span>;
  };

  const formatGBP = (n) => `£${Number(n).toFixed(2)}`;

  const pricingLabel = (u) => {
    const p = u.monthly_pricing;
    if (!p) return null;
    if (p.is_comp) {
      return (
        <span className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
          Comp · £0
        </span>
      );
    }
    if (p.discount_active && p.current_amount !== p.next_amount) {
      // First-month discount: show "£X.XX → £Y.YY/mo"
      return (
        <span
          className="text-[10px] font-medium text-emerald-400"
          data-testid={`user-monthly-${u.email}`}
          title={`${p.discount_percentage}% off first month (${p.discount_code})`}
        >
          {formatGBP(p.current_amount)} → {formatGBP(p.next_amount)}/mo
        </span>
      );
    }
    if (p.discount_active) {
      // Forever discount
      return (
        <span
          className="text-[10px] font-medium text-emerald-400"
          data-testid={`user-monthly-${u.email}`}
          title={`${p.discount_percentage}% off forever (${p.discount_code})`}
        >
          {formatGBP(p.current_amount)}/mo
          <span className="ml-1 line-through text-zinc-600">{formatGBP(p.base_amount)}</span>
        </span>
      );
    }
    return (
      <span className="text-[10px] text-zinc-400 font-medium" data-testid={`user-monthly-${u.email}`}>
        {formatGBP(p.current_amount)}/mo
      </span>
    );
  };

  const signupAffiliateBadge = (u) => {
    const a = u.signup_affiliate;
    if (!a) return null;
    const label = a.name ? `${a.name} · ${a.code}` : a.code;
    return (
      <span
        className="ml-2 inline-flex items-center gap-1 text-[10px] text-pink-400 bg-pink-500/10 px-1.5 py-0.5 rounded"
        title={a.email ? `Affiliate: ${a.email}` : `Affiliate code: ${a.code}`}
        data-testid={`signup-affiliate-${u.email}`}
      >
        <Tag className="w-2.5 h-2.5" />
        via {label}
      </span>
    );
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <Input
            placeholder="Search by name or email..."
            className="pl-10 bg-[#0a0a0f] border-[#1e1e2e] text-white"
            value={search}
            onChange={e => setSearch(e.target.value)}
            data-testid="admin-user-search"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCreateUser(true)}
          className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10"
          data-testid="open-create-user"
        >
          <UserPlus className="w-4 h-4 mr-1.5" />
          New user
        </Button>
        <Button variant="outline" size="sm" onClick={fetchUsers} className="border-[#1e1e2e] text-zinc-400 hover:text-white">
          <RotateCcw className="w-4 h-4" />
        </Button>
      </div>

      <p className="text-xs text-zinc-500">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</p>

      <div className="space-y-2">
        {filtered.map(u => (
          <Card key={u.id} className="bg-[#12121a] border-[#1e1e2e] overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-4 text-left hover:bg-[#16161f] transition-colors"
              onClick={() => setExpanded(expanded === u.id ? null : u.id)}
              data-testid={`user-row-${u.email}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#1e1e2e] flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-zinc-400">{(u.name || '?')[0].toUpperCase()}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {u.name}
                    {u.is_admin && <span className="ml-2 text-[10px] text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">ADMIN</span>}
                    {signupAffiliateBadge(u)}
                    {dormantBadge(u.last_login_at)}
                  </p>
                  <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-right hidden sm:flex sm:flex-col sm:items-end">
                  <p className="text-xs">{subLabel(u)}</p>
                  {pricingLabel(u)}
                </div>
                {expanded === u.id ? <ChevronUp className="w-4 h-4 text-zinc-600" /> : <ChevronDown className="w-4 h-4 text-zinc-600" />}
              </div>
            </button>

            {expanded === u.id && (
              <div className="px-4 pb-4 pt-1 border-t border-[#1e1e2e] space-y-3" data-testid={`user-details-${u.email}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div><span className="text-zinc-500">Plan:</span> <span className="text-white ml-1">{subLabel(u)}</span></div>
                  <div><span className="text-zinc-500">Active:</span> <span className={`ml-1 ${u.subscription_active ? 'text-emerald-400' : 'text-red-400'}`}>{u.subscription_active ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-zinc-500">Affiliate:</span> <span className="text-white ml-1">{u.is_affiliate ? u.affiliate_code : 'No'}</span></div>
                  <div><span className="text-zinc-500">Joined:</span> <span className="text-white ml-1">{u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB') : '—'}</span></div>
                  <div>
                    <span className="text-zinc-500">This month:</span>{' '}
                    <span className="text-cyan-400 font-medium ml-1" data-testid={`user-today-usage-${u.email}`}>
                      {u.this_month_usage_count ?? u.today_usage_count ?? 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Lifetime:</span>{' '}
                    <span className="text-white ml-1" data-testid={`user-lifetime-${u.email}`}>
                      {u.lifetime_generations ?? 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Est. AI spend:</span>{' '}
                    <span
                      className={`ml-1 font-medium ${
                        (u.estimated_lifetime_cost_gbp || 0) > 10 ? 'text-rose-400' : 'text-zinc-300'
                      }`}
                      data-testid={`user-ai-cost-${u.email}`}
                      title="Lifetime generations × £0.025 estimated cost per Claude call (with max_tokens cap)"
                    >
                      £{(u.estimated_lifetime_cost_gbp ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Last login:</span>{' '}
                    <span className="text-white ml-1">{formatRelative(u.last_login_at)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Last gen:</span>{' '}
                    <span className="text-white ml-1">{formatRelative(u.last_generation_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2 pt-1 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs"
                    onClick={() => resetPassword(u.id, u.email)}
                    disabled={resetting === u.id}
                    data-testid={`reset-pw-${u.email}`}
                  >
                    {resetting === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <KeyRound className="w-3 h-3 mr-1" />}
                    Reset Password
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs"
                    onClick={() => resendLogin(u.id, u.email)}
                    disabled={resending === u.id}
                    data-testid={`resend-login-${u.email}`}
                  >
                    {resending === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                    Send Login
                  </Button>

                  {/* Cancel subscription — shown whenever there's a Stripe sub on file,
                      regardless of plan type so the admin can always intervene. */}
                  {u.stripe_subscription_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                      onClick={() => cancelSub(u.id, u.email)}
                      disabled={cancelling === u.id}
                      data-testid={`cancel-sub-${u.email}`}
                    >
                      {cancelling === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                      Cancel Subscription
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                    onClick={() => setGrantOpenFor(grantOpenFor === u.id ? null : u.id)}
                    disabled={granting === u.id}
                    data-testid={`grant-plan-${u.email}`}
                    title="Grant a free Basic/Pro plan without charging"
                  >
                    {granting === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Gift className="w-3 h-3 mr-1" />}
                    Grant Plan
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className={`text-xs ${u.is_admin
                      ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                      : 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10'}`}
                    onClick={() => toggleAdmin(u.id, u.email, u.is_admin)}
                    disabled={togglingAdmin === u.id}
                    data-testid={`toggle-admin-${u.email}`}
                  >
                    {togglingAdmin === u.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : u.is_admin ? (
                      <ShieldOff className="w-3 h-3 mr-1" />
                    ) : (
                      <ShieldCheck className="w-3 h-3 mr-1" />
                    )}
                    {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className={`text-xs ${u.affiliate_always_eligible
                      ? 'border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10'
                      : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800/40'}`}
                    onClick={() => toggleAffiliateEligibility(u.id, u.email, u.affiliate_always_eligible)}
                    disabled={togglingElig === u.id}
                    data-testid={`toggle-affiliate-eligibility-${u.email}`}
                    title="When ON, this user earns affiliate commissions even without an active plan."
                  >
                    {togglingElig === u.id ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Award className="w-3 h-3 mr-1" />
                    )}
                    {u.affiliate_always_eligible ? 'Remove Affiliate Override' : 'Make Always Eligible'}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-700/40 text-red-500 hover:bg-red-500/10 text-xs ml-auto"
                    onClick={() => deleteUser(u.id, u.email)}
                    disabled={deleting === u.id}
                    data-testid={`delete-user-${u.email}`}
                  >
                    {deleting === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                    Delete User
                  </Button>
                </div>

                {/* Grant plan inline form */}
                {grantOpenFor === u.id && (
                  <GrantPlanForm
                    user={u}
                    busy={granting === u.id}
                    onCancel={() => setGrantOpenFor(null)}
                    onConfirm={(plan, days, reason, sendEmail) => grantPlan(u.id, u.email, plan, days, reason, sendEmail)}
                  />
                )}

                {/* Discount controls */}
                <div className="pt-3 border-t border-[#1e1e2e] space-y-2">
                  {u.discount_code ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs text-zinc-400">
                        <span className="text-zinc-500">Active discount:</span>
                        <span className="ml-2 font-mono text-cyan-400">{u.discount_code}</span>
                        <span className="ml-2 text-white">{u.discount_percentage}% off</span>
                        <span className="ml-2 text-zinc-500">
                          ({u.discount_duration === 'forever' ? 'every month' : 'first month'})
                        </span>
                        {u.discount_stripe_applied ? (
                          <span className="ml-2 text-emerald-400">Synced to Stripe</span>
                        ) : (
                          <span className="ml-2 text-amber-400">Local only</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                        onClick={() => removeDiscount(u.id, u.email)}
                        disabled={removingDiscount === u.id}
                        data-testid={`remove-discount-${u.email}`}
                      >
                        {removingDiscount === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                        Remove discount
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-zinc-500">Apply discount:</span>
                      <select
                        className="bg-[#0a0a0f] border border-[#1e1e2e] text-white text-xs rounded-md px-2 py-1.5 min-w-[180px]"
                        value={selectedCode[u.id] || ''}
                        onChange={e => setSelectedCode(prev => ({ ...prev, [u.id]: e.target.value }))}
                        data-testid={`discount-select-${u.email}`}
                      >
                        <option value="">— Choose code —</option>
                        {codes.map(c => (
                          <option key={c.id} value={c.code}>
                            {c.code} ({c.percentage}% {c.duration === 'forever' ? 'every month' : 'first month'})
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                        onClick={() => applyDiscount(u.id, u.email)}
                        disabled={applyingDiscount === u.id || !selectedCode[u.id]}
                        data-testid={`apply-discount-${u.email}`}
                      >
                        {applyingDiscount === u.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                        Apply
                      </Button>
                      {!u.stripe_subscription_id && (
                        <span className="text-[10px] text-amber-400">No active Stripe sub — will save locally</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      <CreateUserModal
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onCreated={() => { fetchUsers(); onRefresh(); }}
      />
    </div>
  );
};

/* ───── Affiliates Tab ───── */
const AffiliatesTab = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const fetchAffiliates = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/affiliates`, { headers: authHeaders() });
      setAffiliates(res.data.affiliates);
    } catch { toast.error('Failed to load affiliates'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAffiliates(); }, []);

  const markPaid = async (affiliateId, email, amount) => {
    setPaying(affiliateId);
    try {
      const res = await axios.post(`${API}/api/admin/affiliates/${affiliateId}/mark-paid`, { amount }, { headers: authHeaders() });
      toast.success(res.data.message);
      fetchAffiliates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as paid');
    } finally { setPaying(null); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;
  if (!affiliates.length) return <p className="text-center text-zinc-500 py-12">No affiliates yet</p>;

  return (
    <div className="space-y-2">
      <div
        className="rounded-md bg-[#0d1620] border border-cyan-500/20 px-3 py-2 mb-3 text-[11px] text-zinc-400 leading-relaxed"
        data-testid="affiliates-commission-info"
      >
        Every affiliate earns <span className="text-cyan-400 font-medium">30%</span> on their own sales.
        Their recruiter earns <span className="text-cyan-400 font-medium">7%</span> on top, and whoever recruited{' '}
        <em>them</em> earns <span className="text-cyan-400 font-medium">3%</span>. Beyond that, no commission flows up.
      </div>
      {affiliates.map(a => {
        const elig = a.eligibility || { eligible: true, status: 'active' };
        const statusBadge = (() => {
          if (elig.status === 'active') return { label: 'Active', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' };
          if (elig.status === 'override') return { label: 'Always Eligible', cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' };
          if (elig.status === 'grace') return { label: 'Grace period', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/30' };
          return { label: 'Ineligible', cls: 'bg-rose-500/10 text-rose-400 border-rose-500/30' };
        })();
        return (
        <Card key={a.id} className="bg-[#12121a] border-[#1e1e2e] p-4" data-testid={`affiliate-row-${a.affiliate_code}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-medium text-white">{a.user_name || a.user_email}</p>
                <span
                  className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusBadge.cls}`}
                  data-testid={`affiliate-eligibility-${a.affiliate_code}`}
                >
                  {statusBadge.label}
                </span>
              </div>
              <p className="text-xs text-zinc-500">{a.user_email}</p>
              <p className="text-[11px] text-zinc-500 mt-0.5" data-testid={`affiliate-recruited-by-${a.affiliate_code}`}>
                {a.recruited_by ? (
                  <>
                    Recruited by{' '}
                    <span className="text-zinc-300">{a.recruited_by.name}</span>
                    <span className="font-mono text-cyan-500/70 ml-1">({a.recruited_by.code})</span>
                  </>
                ) : (
                  <span className="text-zinc-600">Direct signup</span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <div className="text-center">
                <p className="text-zinc-500">Code</p>
                <p className="font-mono text-cyan-400">{a.affiliate_code}</p>
              </div>
              <div className="text-center">
                <p className="text-zinc-500">Referrals</p>
                <p className="text-white">{a.total_referrals}</p>
              </div>
              <div className="text-center">
                <p className="text-zinc-500">Pending</p>
                <p className="text-amber-400">£{(a.pending_earnings || 0).toFixed(2)}</p>
              </div>
              {(a.held_earnings || 0) > 0 && (
                <div className="text-center">
                  <p className="text-zinc-500">On hold</p>
                  <p className="text-rose-400">£{(a.held_earnings || 0).toFixed(2)}</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-zinc-500">Paid</p>
                <p className="text-emerald-400">£{(a.paid_earnings || 0).toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-zinc-500">Total</p>
                <p className="text-white">£{(a.total_earnings || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
          {(a.pending_earnings || 0) > 0 && (
            <div className="mt-3 pt-3 border-t border-[#1e1e2e] flex items-center justify-between">
              <p className="text-xs text-zinc-400">
                £{(a.pending_earnings || 0).toFixed(2)} pending payout
                {!elig.eligible && (
                  <span className="ml-2 text-rose-400">· payout blocked — affiliate is {elig.status}</span>
                )}
              </p>
              <Button
                size="sm"
                variant="outline"
                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs disabled:opacity-40"
                onClick={() => markPaid(a.id, a.user_email, a.pending_earnings)}
                disabled={paying === a.id || !elig.eligible}
                data-testid={`mark-paid-${a.affiliate_code}`}
                title={!elig.eligible ? 'Affiliate must reactivate their plan (or be flagged always-eligible) to be paid' : ''}
              >
                {paying === a.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                Mark as Paid
              </Button>
            </div>
          )}
        </Card>
        );
      })}
    </div>
  );
};

/* ───── Settings Tab ───── */
const SettingsTab = () => {
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error('Enter your current password'); return; }
    if (!email && !newPassword) { toast.error('No changes to save'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings`, {
        email: email || undefined,
        password: newPassword || undefined,
        current_password: currentPassword
      }, { headers: authHeaders() });
      toast.success('Settings updated');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update settings');
    } finally { setSaving(false); }
  };

  return (
    <>
    <Card className="bg-[#12121a] border-[#1e1e2e] p-6 max-w-md">
      <h3 className="text-lg font-bold text-white mb-4">Admin Account Settings</h3>
      <form onSubmit={handleSave} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-zinc-400">New Email (leave blank to keep current)</Label>
          <Input
            type="email"
            className="bg-[#0a0a0f] border-[#1e1e2e] text-white"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="New admin email"
            data-testid="admin-settings-email"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-400">New Password (leave blank to keep current)</Label>
          <div className="relative">
            <Input
              type={showPw ? 'text' : 'password'}
              className="bg-[#0a0a0f] border-[#1e1e2e] text-white pr-10"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="New password (min 6 chars)"
              data-testid="admin-settings-new-pw"
            />
            <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-zinc-400" onClick={() => setShowPw(!showPw)}>
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="space-y-2 pt-2 border-t border-[#1e1e2e]">
          <Label className="text-zinc-400">Current Password (required to save)</Label>
          <Input
            type="password"
            className="bg-[#0a0a0f] border-[#1e1e2e] text-white"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            required
            data-testid="admin-settings-current-pw"
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-red-600 hover:bg-red-700 text-white"
          disabled={saving}
          data-testid="admin-settings-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </form>
    </Card>
    <EmailDiagnosticsCard />
    </>
  );
};


const EmailDiagnosticsCard = () => {
  const [target, setTarget] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/test-email`,
        { to: target.trim() || undefined },
        { headers: authHeaders() }
      );
      setResult(res.data);
      if (res.data.success) toast.success(`Test email sent to ${res.data.to}`);
      else toast.error(res.data.error || res.data.note || 'Test email failed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test email failed');
      setResult({ success: false, error: err.message });
    } finally { setSending(false); }
  };

  return (
    <Card className="bg-[#12121a] border-[#1e1e2e] p-6 max-w-md mt-6" data-testid="email-diagnostics-card">
      <h3 className="text-lg font-bold text-white mb-1">Email Diagnostics</h3>
      <p className="text-xs text-zinc-500 mb-4">
        Send a one-off test email from this environment to verify SMTP delivery.
        Use this on production to confirm verification + welcome emails are reaching inboxes.
      </p>
      <form onSubmit={handleSend} className="space-y-3">
        <div className="space-y-2">
          <Label className="text-zinc-400">Send test to (leave blank to send to your admin email)</Label>
          <Input
            type="email"
            className="bg-[#0a0a0f] border-[#1e1e2e] text-white"
            value={target}
            onChange={e => setTarget(e.target.value)}
            placeholder="you@example.com"
            data-testid="admin-test-email-input"
          />
        </div>
        <Button
          type="submit"
          disabled={sending}
          className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
          data-testid="admin-send-test-email-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Send test email
        </Button>
      </form>

      {result && (
        <div className="mt-4 p-3 rounded-lg bg-[#0a0a0f] border border-[#1e1e2e] text-xs space-y-1 font-mono">
          <p className={result.success ? 'text-emerald-400' : 'text-red-400'}>
            {result.success ? 'SENT' : 'FAILED'}
          </p>
          {result.smtp && (
            <>
              <p className="text-zinc-400">host: <span className="text-white">{result.smtp.host}</span></p>
              <p className="text-zinc-400">port: <span className="text-white">{result.smtp.port}</span></p>
              <p className="text-zinc-400">user: <span className="text-white">{result.smtp.username || 'MISSING'}</span></p>
              <p className="text-zinc-400">ssl:  <span className="text-white">{String(result.smtp.use_ssl)}</span></p>
              {typeof result.smtp.password_set === 'boolean' && (
                <p className="text-zinc-400">password set: <span className="text-white">{String(result.smtp.password_set)}</span></p>
              )}
            </>
          )}
          {result.error && <p className="text-red-400 mt-2">{result.error}</p>}
          {result.note && <p className="text-zinc-400 mt-2">{result.note}</p>}
        </div>
      )}
    </Card>
  );
};

/* ───── Discount Codes Tab ───── */
const DiscountCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [newPct, setNewPct] = useState('');
  const [newDuration, setNewDuration] = useState('first_month');
  const [newMaxUses, setNewMaxUses] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/discount-codes`, { headers: authHeaders() });
      setCodes(res.data.codes);
    } catch { toast.error('Failed to load codes'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCodes(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/discount-codes`, {
        code: newCode, percentage: parseInt(newPct), duration: newDuration, max_uses: parseInt(newMaxUses) || 0
      }, { headers: authHeaders() });
      toast.success('Discount code created');
      setNewCode(''); setNewPct(''); setNewMaxUses('');
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const toggleCode = async (id) => {
    try {
      await axios.post(`${API}/api/admin/discount-codes/${id}/toggle`, {}, { headers: authHeaders() });
      fetchCodes();
    } catch { toast.error('Failed to toggle'); }
  };

  const deleteCode = async (id) => {
    try {
      await axios.delete(`${API}/api/admin/discount-codes/${id}`, { headers: authHeaders() });
      toast.success('Code deleted');
      fetchCodes();
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-6">
      {/* Create new code */}
      <Card className="bg-[#12121a] border-[#1e1e2e] p-6">
        <h3 className="text-lg font-bold text-white mb-4">Create Discount Code</h3>
        <form onSubmit={handleCreate} className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Input placeholder="CODE" className="bg-[#0a0a0f] border-[#1e1e2e] text-white uppercase" value={newCode} onChange={e => setNewCode(e.target.value)} required data-testid="discount-code-input" />
          <Input type="number" placeholder="% off" className="bg-[#0a0a0f] border-[#1e1e2e] text-white" value={newPct} onChange={e => setNewPct(e.target.value)} required min="1" max="100" data-testid="discount-pct-input" />
          <select className="bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm rounded-md px-3" value={newDuration} onChange={e => setNewDuration(e.target.value)} data-testid="discount-duration">
            <option value="first_month">First month only</option>
            <option value="forever">Every month</option>
          </select>
          <Input type="number" placeholder="Max uses (0=unlimited)" className="bg-[#0a0a0f] border-[#1e1e2e] text-white" value={newMaxUses} onChange={e => setNewMaxUses(e.target.value)} />
          <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700 text-white" disabled={creating} data-testid="discount-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
          </Button>
        </form>
      </Card>

      {/* Existing codes */}
      {codes.length === 0 ? (
        <p className="text-center text-zinc-500 py-8">No discount codes yet</p>
      ) : (
        <div className="space-y-2">
          {codes.map(c => (
            <Card key={c.id} className="bg-[#12121a] border-[#1e1e2e] p-4 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <span className="font-mono font-bold text-cyan-400 text-sm">{c.code}</span>
                <span className="text-white font-medium text-sm">{c.percentage}% off</span>
                <span className="text-xs text-zinc-500">{c.duration === 'forever' ? 'Every month' : 'First month'}</span>
                <span className="text-xs text-zinc-500">Used: {c.times_used}{c.max_uses > 0 ? `/${c.max_uses}` : ''}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
                  {c.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-[#1e1e2e] text-zinc-400 text-xs" onClick={() => toggleCode(c.id)}>
                  {c.active ? 'Disable' : 'Enable'}
                </Button>
                <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 text-xs" onClick={() => deleteCode(c.id)}>
                  Delete
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

/* ───── Homepage TikTok Videos ───── */
const HomepageVideosCard = () => {
  const [videos, setVideos] = useState(['', '', '']);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/homepage-videos`, { headers: authHeaders() });
        const v = res.data.videos || [];
        setVideos([v[0] || '', v[1] || '', v[2] || '']);
      } catch { /* defaults */ }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/admin/homepage-videos`,
        { videos: videos.filter((x) => x.trim()) },
        { headers: authHeaders() }
      );
      const v = res.data.videos || [];
      setVideos([v[0] || '', v[1] || '', v[2] || '']);
      toast.success('Homepage videos saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-[#12121a] border-[#1e1e2e] p-6">
      <h3 className="text-lg font-bold text-white mb-2">Homepage TikTok Videos</h3>
      <p className="text-sm text-zinc-400 mb-4">
        Paste up to 3 full TikTok video URLs (e.g. https://www.tiktok.com/@user/video/1234567890). These appear in the homepage showcase. Leave blank to show placeholders.
      </p>
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <input
            key={i}
            value={videos[i]}
            onChange={(e) => { const n = [...videos]; n[i] = e.target.value; setVideos(n); }}
            placeholder={`TikTok URL ${i + 1}`}
            data-testid={`homepage-video-${i}`}
            className="w-full p-3 rounded-lg bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm focus:outline-none focus:border-cyan-500/50"
          />
        ))}
      </div>
      <Button
        onClick={save}
        className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700 text-white"
        disabled={saving}
        data-testid="homepage-videos-save"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Save Videos
      </Button>
    </Card>
  );
};

/* ───── AI Settings Tab ───── */
const GUIDE_FIELDS = [
  { key: 'script_persona', label: 'Persona (system prompt)', hint: 'Who the AI is writing as. {region} is replaced with UK or US.' },
  { key: 'region_tone_uk', label: 'Region tone — UK', hint: 'Voice, spelling and currency rules for UK scripts.' },
  { key: 'region_tone_us', label: 'Region tone — US', hint: 'Voice, spelling and currency rules for US scripts.' },
  { key: 'no_brands_additions', label: 'Extra content rules', hint: 'Added directly after the core no-brands rules.' },
  { key: 'banned_phrases', label: 'Banned phrases', hint: 'Sent with every generation and enforced in the edit pass.' },
  { key: 'narrator_ugc', label: 'Narrator style — UGC creator (default)', hint: '' },
  { key: 'narrator_authority', label: 'Narrator style — Expert authority', hint: '' },
  { key: 'narrator_sceptical', label: 'Narrator style — Sceptical review', hint: '' },
  { key: 'structure_bof', label: 'Script structure — BOF', hint: 'Bottom of funnel. Word count guidance included.' },
  { key: 'structure_mof', label: 'Script structure — MOF', hint: 'Middle of funnel.' },
  { key: 'structure_tof', label: 'Script structure — TOF', hint: 'Top of funnel.' },
  { key: 'structure_custom', label: 'Script structure — Custom length', hint: 'Used when no script type is chosen. {seconds} is replaced with the chosen length.' },
  { key: 'edit_pass_instruction', label: 'Edit pass instruction', hint: 'Second pass that rewrites every script to sound human. {region} and {target_word_count} are replaced automatically.' },
];

const GuideField = ({ field, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-white mb-1">{field.label}</label>
    {field.hint ? <p className="text-xs text-zinc-500 mb-2">{field.hint}</p> : null}
    <textarea
      className="w-full min-h-[110px] p-3 rounded-lg bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm resize-y focus:outline-none focus:border-cyan-500/50"
      value={value || ''}
      onChange={e => onChange(field.key, e.target.value)}
      data-testid={`ai-guide-${field.key.replace(/_/g, '-')}`}
    />
  </div>
);

const AISettingsTab = () => {
  const [region, setRegion] = useState('uk');
  const [customInstructions, setCustomInstructions] = useState('');
  const [appealGuidelines, setAppealGuidelines] = useState('');
  const [guide, setGuide] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/ai-settings`, { headers: authHeaders() });
        setRegion(res.data.region || 'uk');
        setCustomInstructions(res.data.custom_instructions || '');
        setAppealGuidelines(res.data.appeal_guidelines || '');
        const g = {};
        GUIDE_FIELDS.forEach(f => { g[f.key] = res.data[f.key] || ''; });
        setGuide(g);
      } catch { /* defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  const updateGuide = (key, val) => setGuide(prev => ({ ...prev, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/ai-settings`, {
        region,
        custom_instructions: customInstructions,
        appeal_guidelines: appealGuidelines,
        ...guide,
      }, { headers: authHeaders() });
      toast.success('AI settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="max-w-2xl space-y-6">
      <Card className="bg-[#12121a] border-[#1e1e2e] p-6">
        <h3 className="text-lg font-bold text-white mb-4">Content Region</h3>
        <p className="text-sm text-zinc-400 mb-4">
          Controls the tone, spelling, cultural references, and currency used in generated content.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setRegion('uk')}
            className={`flex-1 p-4 rounded-lg border text-center transition-colors ${region === 'uk' ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-[#1e1e2e] text-zinc-500 hover:border-zinc-600'}`}
            data-testid="ai-region-uk"
          >
            <span className="text-2xl mb-2 block">🇬🇧</span>
            <span className="font-medium text-sm">UK Style</span>
            <p className="text-xs text-zinc-500 mt-1">British tone, £ GBP</p>
          </button>
          <button
            onClick={() => setRegion('usa')}
            className={`flex-1 p-4 rounded-lg border text-center transition-colors ${region === 'usa' ? 'border-cyan-500 bg-cyan-500/10 text-white' : 'border-[#1e1e2e] text-zinc-500 hover:border-zinc-600'}`}
            data-testid="ai-region-usa"
          >
            <span className="text-2xl mb-2 block">🇺🇸</span>
            <span className="font-medium text-sm">USA Style</span>
            <p className="text-xs text-zinc-500 mt-1">American tone, $ USD</p>
          </button>
        </div>
      </Card>

      <Card className="bg-[#12121a] border-[#1e1e2e] p-6 space-y-5">
        <div>
          <h3 className="text-lg font-bold text-white mb-1">TikTok Script Generation Guide</h3>
          <p className="text-sm text-zinc-400">
            The structure the AI uses to write every TikTok script. Edit any block below —
            changes apply to the next generation. Placeholders like <span className="text-cyan-400">{'{region}'}</span>,
            {' '}<span className="text-cyan-400">{'{seconds}'}</span> and <span className="text-cyan-400">{'{target_word_count}'}</span> are filled in automatically.
          </p>
        </div>
        {GUIDE_FIELDS.map(f => (
          <GuideField key={f.key} field={f} value={guide[f.key]} onChange={updateGuide} />
        ))}
      </Card>

      <Card className="bg-[#12121a] border-[#1e1e2e] p-6">
        <h3 className="text-lg font-bold text-white mb-2">Custom AI Instructions</h3>
        <p className="text-sm text-zinc-400 mb-4">
          Extra instructions injected into every generation, on top of the guide above. Use this for one-off tweaks or temporary rules.
        </p>
        <textarea
          className="w-full min-h-[160px] p-4 rounded-lg bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm resize-y focus:outline-none focus:border-cyan-500/50"
          placeholder="Example:&#10;- Mention free delivery where possible&#10;- Lean into seasonal angles this month"
          value={customInstructions}
          onChange={e => setCustomInstructions(e.target.value)}
          data-testid="ai-custom-instructions"
        />
      </Card>

      <HomepageVideosCard />

      <Card className="bg-[#12121a] border-[#1e1e2e] p-6">
        <h3 className="text-lg font-bold text-white mb-2">Violation Appeal Guidelines</h3>
        <p className="text-sm text-zinc-400 mb-4">
          Extra up-to-date TikTok Shop appeal rules and "what makes appeals succeed" notes. These feed every Violation Wizard assessment on top of the seeded per-type policy summaries.
        </p>
        <textarea
          className="w-full min-h-[160px] p-4 rounded-lg bg-[#0a0a0f] border border-[#1e1e2e] text-white text-sm resize-y focus:outline-none focus:border-cyan-500/50"
          placeholder="Example:&#10;- Appeals mentioning a specific timestamp where the product is shown succeed more often&#10;- Never advise claiming medical certification&#10;- Keep the tone factual, no apologies"
          value={appealGuidelines}
          onChange={e => setAppealGuidelines(e.target.value)}
          data-testid="ai-appeal-guidelines"
        />
      </Card>

      <Button
        onClick={handleSave}
        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
        disabled={saving}
        data-testid="ai-settings-save"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
        Save AI Settings
      </Button>
    </div>
  );
};

/* ───── Main Dashboard ───── */
const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/stats`, { headers: authHeaders() });
      setStats(res.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('kylo_admin_token');
        navigate('/backendcontrol');
      }
    } finally { setStatsLoading(false); }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate('/backendcontrol'); return; }
    fetchStats();
  }, [navigate, fetchStats]);

  const logout = () => {
    localStorage.removeItem('kylo_admin_token');
    navigate('/backendcontrol');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'affiliates', label: 'Affiliates', icon: Share2 },
    { id: 'discounts', label: 'Discounts', icon: CreditCard },
    { id: 'ai', label: 'AI Settings', icon: Star },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <header className="border-b border-[#1e1e2e] bg-[#0a0a0f]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-red-400" />
            <span className="font-bold text-white text-sm">Poettik Admin</span>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigate('/backendcontrol/creator')} className="text-zinc-500 hover:text-white" data-testid="admin-creator-link">
              <Search className="w-4 h-4 mr-2" /> Creator
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-500 hover:text-white" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#12121a] rounded-lg p-1 border border-[#1e1e2e] w-fit">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors ${tab === t.id ? 'bg-[#1e1e2e] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'overview' && <OverviewTab stats={stats} loading={statsLoading} />}
        {tab === 'users' && <UsersTab onRefresh={fetchStats} />}
        {tab === 'affiliates' && <AffiliatesTab />}
        {tab === 'discounts' && <DiscountCodesTab />}
        {tab === 'ai' && <AISettingsTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

export default AdminDashboardPage;
